import { AR } from '../i18n/ar';
import { EN } from '../i18n/en';

/**
 * Three languages: French (the source), Arabic and English.
 *
 * The French text is the key. `t('Se connecter')` returns the French when the
 * site is in French and looks the phrase up otherwise, falling back to French
 * when a translation is missing — so a new string added without its
 * translations shows in French rather than as a blank or a code.
 *
 * The language is read once, at startup, and changing it reloads the page. That
 * is what lets `t` be a plain function usable anywhere — in components, in
 * label maps built at import time, in a toast — with no provider to thread
 * through and no stale label anywhere after a switch.
 */

export type Language = 'fr' | 'ar' | 'en';

const KEY = 'bedly.lang';
const DICTIONARIES: Record<Exclude<Language, 'fr'>, Record<string, string>> = { ar: AR, en: EN };

const isLanguage = (value: unknown): value is Language => value === 'fr' || value === 'ar' || value === 'en';

/*
 * Mirrored into a cookie so the server can write the page's title, language
 * and direction before any JavaScript runs — no French flash for an Arabic
 * reader, and a shared link that previews in the sharer's language.
 */
function rememberLanguage(code: Language): void {
  try {
    localStorage.setItem(KEY, code);
  } catch {
    // No storage: the cookie below still carries it.
  }
  document.cookie = `${KEY}=${code}; path=/; max-age=31536000; samesite=lax`;
}

function detect(): Language {
  /*
   * A link that names its language wins: /recherche?lang=ar is the Arabic
   * page search engines list, and opening it must show Arabic — and keep
   * showing Arabic as the reader moves on.
   */
  const fromUrl = new URLSearchParams(window.location.search).get('lang');
  if (isLanguage(fromUrl)) {
    rememberLanguage(fromUrl);
    return fromUrl;
  }
  try {
    const saved = localStorage.getItem(KEY);
    if (isLanguage(saved)) {
      // Older visits stored only localStorage; the cookie catches up.
      if (!document.cookie.includes(`${KEY}=${saved}`)) rememberLanguage(saved);
      return saved;
    }
  } catch {
    // No storage: fall through to the browser's own preference.
  }
  // A first visit follows the browser: Arabic or English if that is what it
  // asks for, French otherwise — French is the site's own language in Morocco.
  const preferred = (navigator.languages ?? [navigator.language]).map((code) => code.slice(0, 2));
  const detected = preferred.find(isLanguage) ?? 'fr';
  // Only the cookie, so the server's next page matches; localStorage is kept
  // for a choice the person actually made.
  document.cookie = `${KEY}=${detected}; path=/; max-age=31536000; samesite=lax`;
  return detected;
}

export const language: Language = typeof window === 'undefined' ? 'fr' : detect();
export const isRtl = language === 'ar';

/** Set on the document before the first paint, so Arabic lays out right to left. */
export function applyDocumentLanguage(): void {
  document.documentElement.lang = language;
  document.documentElement.dir = isRtl ? 'rtl' : 'ltr';
}

/**
 * Translate. `params` fills `{name}` placeholders after translation, so a
 * sentence keeps its own word order in each language:
 *   t('{count} logements', { count: 3 })
 */
export function t(french: string, params?: Record<string, string | number>): string {
  const text = language === 'fr' ? french : (DICTIONARIES[language][french] ?? french);
  if (!params) return text;
  return text.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in params ? String(params[name]) : match,
  );
}

/**
 * A label map whose values translate when read. Used for the maps built once at
 * import time (rental types, statuses, amenities), so every place that reads
 * MODE_LABEL[mode] gets the current language without changing.
 */
export function translatedRecord<T extends Record<string, string>>(record: T): T {
  return new Proxy(record, {
    get(target, key) {
      const value = Reflect.get(target, key);
      return typeof value === 'string' ? t(value) : value;
    },
  });
}

export const LANGUAGES: { code: Language; label: string; short: string }[] = [
  { code: 'fr', label: 'Français', short: 'FR' },
  { code: 'ar', label: 'العربية', short: 'ع' },
  { code: 'en', label: 'English', short: 'EN' },
];

/**
 * Switch language: remembered in this browser, saved to the account when there
 * is one (so emails follow), then a reload to redraw everything in it.
 */
export async function setLanguage(next: Language, saveToAccount?: () => Promise<unknown>): Promise<void> {
  rememberLanguage(next);
  try {
    await saveToAccount?.();
  } catch {
    // The account copy is a convenience; the switch itself must not depend on it.
  }
  // A ?lang= in the address would win over the choice just made, so it goes.
  const url = new URL(window.location.href);
  if (url.searchParams.has('lang')) {
    url.searchParams.delete('lang');
    window.location.replace(url.toString());
  } else {
    window.location.reload();
  }
}
